// App.jsx (saved tours list)
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Search } from "lucide-react"
import SessionAccordion from "./components/SessionAccordion"
import MobileFilterSheet from "./components/MobileFilterSheet"

import {
  useLazyGetSavedToursFilterOptionsQuery,
  useLazyGetSavedToursSessionsJsonQuery,
} from "../../../app/services/book/saved_tours"
import ExtensionConnectPanel from "./components/ExtensionConnectionPanel"

const EMPTY_SET = new Set()

const EMPTY_META = {
  sources: [],
  stars: [],
  mealPlans: [],
  resorts: [],
  priceMin: 0,
  priceMax: 0,
}

const App = ({ isAdmin }) => {
  const { t } = useTranslation()

  const [sort, setSort] = useState("price_asc")
  const [sheetOpen, setSheetOpen] = useState(false)
  const [showPhotos, setShowPhotos] = useState(false)
  const [extensionPanelOpen, setExtensionPanelOpen] = useState(false)
  const [sessionNumberQuery, setSessionNumberQuery] = useState("")

  const [
    fetchSessions,
    { data: sessionsData, isLoading: sessionsLoading, isFetching: sessionsFetching },
  ] = useLazyGetSavedToursSessionsJsonQuery()
  const [fetchFilterOptions, { data: filterOptionsData }] = useLazyGetSavedToursFilterOptionsQuery()

  useEffect(() => {
    fetchFilterOptions({})
  }, [fetchFilterOptions])

  const meta = useMemo(() => {
    const f = filterOptionsData?.filters
    if (!f) return EMPTY_META

    return {
      sources: Array.from(new Set(f.sources.map((s) => s.source_name))),
      stars: Array.from(new Set(f.stars.map((s) => s.value))).sort(
        (a, b) => parseInt(b, 10) - parseInt(a, 10)
      ),
      mealPlans: f.meal_plans.map((m) => m.value),
      resorts: Array.from(new Set(f.resorts.map((r) => r.resort_name))),
      priceMin: f.price_min ?? 0,
      priceMax: f.price_max ?? 0,
    }
  }, [filterOptionsData])

  const [filters, setFilters] = useState({
    sources: EMPTY_SET,
    stars: EMPTY_SET,
    mealPlans: EMPTY_SET,
    resorts: EMPTY_SET,
    maxPrice: null,
    onlyGuaranteed: false,
  })

  useEffect(() => {
    if (meta.priceMax && filters.maxPrice === null) {
      setFilters((f) => ({ ...f, maxPrice: meta.priceMax }))
    }
  }, [meta.priceMax, filters.maxPrice])

  const toggle = (key, value) => {
    setFilters((f) => {
      const next = new Set(f[key])
      next.has(value) ? next.delete(value) : next.add(value)
      return { ...f, [key]: next }
    })
  }
  const setMaxPrice = (v) => setFilters((f) => ({ ...f, maxPrice: v }))
  const setOnlyGuaranteed = (fn) =>
    setFilters((f) => ({
      ...f,
      onlyGuaranteed: typeof fn === "function" ? fn(f.onlyGuaranteed) : fn,
    }))
  const resetFilters = () =>
    setFilters({
      sources: EMPTY_SET,
      stars: EMPTY_SET,
      mealPlans: EMPTY_SET,
      resorts: EMPTY_SET,
      maxPrice: meta.priceMax,
      onlyGuaranteed: false,
    })

  const activeCount =
    filters.sources.size +
    filters.stars.size +
    filters.mealPlans.size +
    filters.resorts.size +
    (filters.maxPrice != null && filters.maxPrice < meta.priceMax ? 1 : 0) +
    (filters.onlyGuaranteed ? 1 : 0)

  const queryParams = useMemo(() => {
    const params = {}
    if (filters.sources.size) params.source_name = Array.from(filters.sources).join(",")
    if (filters.stars.size) params.star_name = Array.from(filters.stars).join(",")
    if (filters.mealPlans.size) params.food_name = Array.from(filters.mealPlans).join(",")
    if (filters.resorts.size) params.resort_name = Array.from(filters.resorts).join(",")
    if (filters.maxPrice != null && filters.maxPrice < meta.priceMax) {
      params.max_price = filters.maxPrice
    }
    if (filters.onlyGuaranteed) params.guaranteed = 1
    if (sessionNumberQuery.trim()) params.session_number = sessionNumberQuery.trim()
    return params
  }, [filters, meta.priceMax, sessionNumberQuery])

  useEffect(() => {
    fetchSessions(queryParams)
  }, [fetchSessions, queryParams])

  const sessions = sessionsData?.sessions || []
  const isInitialLoading = sessionsLoading && sessions.length === 0

  const filterProps = {
    meta,
    filters,
    toggle,
    setMaxPrice,
    setOnlyGuaranteed,
    onReset: resetFilters,
    activeCount,
  }

  return (
    <div className="min-h-screen w-full bg-[#F4F6F9] py-6 md:py-10 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6 flex-wrap">
          <h1 className="text-lg md:text-xl font-semibold text-[#1F2937]">
            {t("saved_tours.title")}
          </h1>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA5B4]"
              />
              <input
                type="text"
                value={sessionNumberQuery}
                onChange={(e) => setSessionNumberQuery(e.target.value)}
                placeholder={t("saved_tours.search_by_session_number")}
                className="w-44 md:w-56 pl-9 pr-3 py-2 rounded-xl bg-white border border-[#E7EAEF] text-sm
                           text-[#26303D] placeholder:text-[#9AA5B4] focus:outline-none focus:ring-1
                           focus:ring-[#FF6B3D] transition-shadow"
              />
            </div>

            <button
              onClick={() => setExtensionPanelOpen(true)}
              className="shrink-0 px-3.5 py-2 rounded-xl bg-white border border-[#E7EAEF] text-sm
                         font-medium text-[#1F2937] hover:bg-[#F4F6F9] transition-colors"
            >
              {t("saved_tours.extension.title")}
            </button>
          </div>
        </div>

        <div className="flex gap-6 items-start">
          {/* <FilterSidebar {...filterProps} /> */}

          <div className="flex-1 min-w-0 flex flex-col gap-4">
            {isInitialLoading && (
              <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
                {t("saved_tours.loading_tours")}
              </div>
            )}

            {sessionsFetching && !isInitialLoading && (
              <div className="text-sm text-[#9AA5B4] px-1">{t("saved_tours.updating_list")}</div>
            )}

            {sessions.map((session, i) => (
              <SessionAccordion
                key={session.session_key}
                session={session}
                sort={sort}
                showPhotos={showPhotos}
                defaultOpen={i === 0}
              />
            ))}

            {!isInitialLoading && sessions.length === 0 && (
              <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
                {t("saved_tours.no_tours")}
              </div>
            )}
          </div>
        </div>
      </div>

      <MobileFilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        resultCount={sessions.length}
        {...filterProps}
      />

      <ExtensionConnectPanel
        open={extensionPanelOpen}
        onClose={() => setExtensionPanelOpen(false)}
      />
    </div>
  )
}

export default App
